import React, { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "./ui/drawer";
import { Button } from "./ui/button";
import { Download, Share2, Plus, Check } from "lucide-react";

function isStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
    );
  } catch (_e) { return false; }
}

function isIOS() {
  try {
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  } catch (_e) { return false; }
}

/**
 * InstallButton — small icon button always present in the dashboard header.
 *
 * Behavior:
 *  - If app is already installed (display-mode: standalone), the button hides itself.
 *  - Otherwise it stays visible permanently. Clicking it:
 *      • triggers the captured `beforeinstallprompt` event when available
 *        (Chrome/Edge/Android), or
 *      • opens a help drawer with platform-specific instructions
 *        (iOS: Share → Add to Home Screen; desktop: open in mobile browser).
 *
 * This component intentionally has NO dismiss-cache so the user can ALWAYS
 * re-trigger the install flow after closing any other prompt.
 */
export default function InstallButton() {
  const { t } = useT();
  const deferred = useRef(null);
  const [hidden, setHidden] = useState(() => isStandalone());
  const [native, setNative] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setHidden(true); return undefined; }
    const onBefore = (e) => {
      e.preventDefault();
      deferred.current = e;
      setNative(true);
    };
    const onInstalled = () => { setHidden(true); deferred.current = null; setNative(false); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden) return null;

  const click = async () => {
    if (native && deferred.current) {
      try {
        deferred.current.prompt();
        const choice = await deferred.current.userChoice;
        if (choice?.outcome === "accepted") {
          setHidden(true);
          deferred.current = null;
          setNative(false);
        }
      } catch (_e) { /* fall through to help */ }
      return;
    }
    setHelpOpen(true);
  };

  const ios = isIOS();

  return (
    <>
      <button
        type="button"
        onClick={click}
        className="w-9 h-9 rounded-full bg-fresh-green text-black shadow-sm shadow-fresh-green/30 flex items-center justify-center active:scale-90 transition"
        aria-label={t("install.btn")}
        title={t("install.btn")}
        data-testid="install-app-btn"
      >
        <Download className="w-4 h-4" strokeWidth={2.5} />
      </button>

      <Drawer open={helpOpen} onOpenChange={setHelpOpen}>
        <DrawerContent data-testid="install-help-drawer">
          <div className="mx-auto w-full max-w-md text-zinc-950">
            <DrawerHeader className="text-left">
              <div className="w-12 h-12 rounded-2xl bg-fresh-green text-black flex items-center justify-center mb-2">
                <Download className="w-5 h-5" strokeWidth={2.6} />
              </div>
              <DrawerTitle className="font-heading text-2xl font-medium">{t("install.title")}</DrawerTitle>
              <DrawerDescription>
                {ios ? t("install.help.ios.intro") : t("install.help.desktop.intro")}
              </DrawerDescription>
            </DrawerHeader>

            <div className="px-4 pb-2 space-y-3">
              {ios ? (
                <ol className="space-y-3" data-testid="install-help-ios">
                  <Step n={1} label={t("install.help.ios.step1")}>
                    <Share2 className="w-5 h-5 text-zinc-700" />
                  </Step>
                  <Step n={2} label={t("install.help.ios.step2")}>
                    <Plus className="w-5 h-5 text-zinc-700" />
                  </Step>
                  <Step n={3} label={t("install.help.ios.step3")}>
                    <Check className="w-5 h-5 text-zinc-700" strokeWidth={3} />
                  </Step>
                </ol>
              ) : (
                <ol className="space-y-3" data-testid="install-help-desktop">
                  <Step n={1} label={t("install.help.desktop.step1")} />
                  <Step n={2} label={t("install.help.desktop.step2")} />
                  <Step n={3} label={t("install.help.desktop.step3")} />
                </ol>
              )}
            </div>

            <DrawerFooter>
              <Button onClick={() => setHelpOpen(false)} className="w-full h-12 rounded-xl bg-zinc-950 text-white" data-testid="install-help-close">
                {t("common.confirm")}
              </Button>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function Step({ n, label, children }) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-zinc-50 border border-zinc-100 p-3">
      <span className="w-7 h-7 rounded-full bg-zinc-950 text-white text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
      <span className="flex-1 text-sm text-zinc-800">{label}</span>
      {children && <span className="w-9 h-9 rounded-xl bg-white border border-zinc-200 flex items-center justify-center shrink-0">{children}</span>}
    </li>
  );
}
